// AuthContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { createAbility, updateAbility } from "../config/acl/ability";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [initialized, setInitialized] = useState(false);
  const [authState, setAuthState] = useState(() => ({
    user: null,
    permissions: [],
    roles: [],
    ability: null,
    isAuthenticated: false,
  }));

  const login = useCallback(
    (data) => {
      const ability = createAbility(data.permissions);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("roles", JSON.stringify(data.roles));
      localStorage.setItem("permissions", JSON.stringify(data.permissions));
      updateAbility(data.permissions);

      setAuthState((prevState) => ({
        ...prevState,
        user: data.user,
        permissions: data.permissions,
        roles: data.roles,
        ability: ability,
        isAuthenticated: true,
      }));
      navigate("/dashboard", { replace: true });
    },
    [navigate]
  );

  const logout = useCallback(() => {
    localStorage.clear();
    setAuthState({
      user: null,
      permissions: [],
      roles: [],
      ability: null,
      isAuthenticated: false,
    });
    navigate("/login", { replace: true });
  }, [navigate]);

  // AuthContext.jsx
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      const storedRoles = localStorage.getItem("roles");
      const storedPermissions = localStorage.getItem("permissions");
      const storedAbility = localStorage.getItem("abilityRules"); // Add this

      if (token) {
        try {
          // Restore ability from localStorage
          const abilityRules = storedAbility ? JSON.parse(storedAbility) : [];
          const abilityInstance = createAbility(abilityRules);
          updateAbility(abilityRules);

          setAuthState({
            user: JSON.parse(storedUser),
            permissions: JSON.parse(storedPermissions),
            roles: JSON.parse(storedRoles),
            ability: abilityInstance,
            isAuthenticated: true,
          });
        } catch (error) {
          console.error("Error initializing auth:", error);
          logout();
        } finally {
          setInitialized(true);
        }
      }
    };

    initializeAuth();
  }, [logout, navigate]);

  const contextValue = {
    ...authState,
    initialized, // Add this
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
